// ─────────────────────────────────────────────────────────────────────────────
// PUTTING A LOOK ON THE SCREEN.
//
// ── THE BUG THIS REPLACES ────────────────────────────────────────────────────
//
// The injector in App.js opened with:
//
//     if (document.getElementById("survai-editorial-theme")) return;
//
// which is correct for "mount once" and wrong for "change". With one hardcoded
// theme nobody could tell; the moment a tenant has a choice, that line means the
// first theme to land wins for the rest of the session and a switch silently does
// nothing. So this writes into the SAME element every time instead of bailing.
// ─────────────────────────────────────────────────────────────────────────────

import { FONT_ELEMENT_ID, STYLE_ELEMENT_ID, themeCss } from "./tokens.js";
import {
  CONTRAST_PAIRS,
  DEFAULT_DENSITY,
  DEFAULT_PRESET,
  densityFor,
  presetFor,
} from "./presets.js";
import { AA_TEXT, checkContrast } from "./contrast.js";

/** Where a per-device preview choice lives. */
export const PREVIEW_KEY = "survai_theme_preview";
export const PREVIEW_DENSITY_KEY = "survai_theme_density_preview";

/**
 * Which look to use, as a pure decision.
 *
 * ORDER, MOST SPECIFIC FIRST:
 *   1. an explicit argument — a picker previewing a choice before it is saved
 *   2. the per-device preview override — how the five directions get evaluated
 *      in the real app without a database write
 *   3. the tenant's saved choice, from `organizations.theme`
 *   4. the look that is already shipping
 *
 * Step 4 is why this is safe to deploy: a tenant with no row value, an org that
 * has not been migrated, a logged-out screen and a server that does not send the
 * field all land on exactly what is on screen today.
 */
export function resolveTheme({ explicit = null, session = null, preview = null } = {}) {
  const key =
    explicit ||
    preview ||
    (session && (session.theme || session.orgTheme || (session.organization && session.organization.theme))) ||
    DEFAULT_PRESET;
  return presetFor(key);
}

export function resolveDensity({ explicit = null, session = null, preview = null } = {}) {
  const key =
    explicit ||
    preview ||
    (session && (session.themeDensity || (session.organization && session.organization.theme_density))) ||
    DEFAULT_DENSITY;
  return densityFor(key);
}

/** The per-device preview choice, or null. Never throws — private mode, blocked storage. */
export function readPreview(storageKey = PREVIEW_KEY) {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(storageKey) || null;
  } catch {
    return null;
  }
}

/** Set or clear the per-device preview choice. Returns true when it stuck. */
export function writePreview(value, storageKey = PREVIEW_KEY) {
  try {
    if (typeof localStorage === "undefined") return false;
    if (value) localStorage.setItem(storageKey, value);
    else localStorage.removeItem(storageKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Every contrast pair in a preset that does not clear the bar.
 *
 * Shared by the test and by any write path that accepts a custom value, so the
 * check that gates a save is the same code as the check that gates the build.
 * Returns [] for a clean preset.
 */
export function contrastFailures(preset, { required = AA_TEXT } = {}) {
  const t = (preset && preset.tokens) || {};
  const out = [];
  for (const pair of CONTRAST_PAIRS) {
    const res = checkContrast(t[pair.fg], t[pair.bg], { required });
    if (!res.ok) {
      out.push({
        preset: preset && preset.key,
        fg: pair.fg,
        bg: pair.bg,
        where: pair.where,
        fgValue: t[pair.fg],
        bgValue: t[pair.bg],
        ratio: res.ratio,
        required: res.required,
        measurable: res.measurable,
      });
    }
  }
  return out;
}

/**
 * Put a look on the document.
 *
 * Idempotent on the same (preset, density): rewrites the element's text, which the
 * browser no-ops when it is unchanged. Returns the preset applied, or null when
 * there is no document (tests, SSR).
 */
export function applyTheme({ explicit = null, session = null, document: doc = null } = {}) {
  const d = doc || (typeof document === "undefined" ? null : document);
  if (!d) return null;

  const preset = resolveTheme({ explicit, session, preview: readPreview() });
  const density = resolveDensity({ session, preview: readPreview(PREVIEW_DENSITY_KEY) });

  // Fonts. Replacing the href rather than appending a second <link> matters:
  // appending leaves the previous theme's families loaded and first in the
  // cascade, so a switch would keep the old display face.
  let link = d.getElementById(FONT_ELEMENT_ID);
  if (preset.fonts) {
    if (!link) {
      for (const [rel, href, cross] of [
        ["preconnect", "https://fonts.googleapis.com", false],
        ["preconnect", "https://fonts.gstatic.com", true],
      ]) {
        const pc = d.createElement("link");
        pc.rel = rel;
        pc.href = href;
        if (cross) pc.crossOrigin = "anonymous";
        d.head.appendChild(pc);
      }
      link = d.createElement("link");
      link.id = FONT_ELEMENT_ID;
      link.rel = "stylesheet";
      d.head.appendChild(link);
    }
    if (link.getAttribute("href") !== preset.fonts) link.setAttribute("href", preset.fonts);
  }

  let style = d.getElementById(STYLE_ELEMENT_ID);
  if (!style) {
    style = d.createElement("style");
    style.id = STYLE_ELEMENT_ID;
    d.head.appendChild(style);
  }
  const css = themeCss(preset, { density: density.scale });
  if (style.textContent !== css) style.textContent = css;

  // ── THE ONE SURFACE ONLY A PHONE HAS ───────────────────────────────────────
  //
  // `<meta name="theme-color">` is what tints the browser's own chrome on a phone — the
  // address bar above the page and the task-switcher card. index.html hardcodes it to
  // #0a1f3d, a navy that matches no preset and nothing else in the product, so the strip
  // of browser directly above a themed app was permanently the wrong colour and no
  // amount of CSS could reach it. It is an attribute, not a custom property, so the
  // token layer cannot touch it and it has to be written here.
  //
  // `nav-from` rather than `bg` or `brand`, because the thing physically adjacent to the
  // browser chrome is the top of the navigation bar — on mobile the sticky header, which
  // starts at nav-from. Anything else leaves a visible seam at the top of the screen.
  //
  // Only ever UPDATED, never created: a page served without the meta is one where the
  // author did not want it, and adding it would change the chrome on a surface nobody
  // asked us to paint.
  const themeColor = d.querySelector('meta[name="theme-color"]');
  const navFrom = preset.tokens && preset.tokens["nav-from"];
  if (themeColor && navFrom && themeColor.getAttribute("content") !== navFrom) {
    themeColor.setAttribute("content", navFrom);
  }

  // So a screenshot, a bug report or a support call can say which look is on.
  if (d.documentElement) {
    d.documentElement.setAttribute("data-survai-theme", preset.key);
    d.documentElement.setAttribute("data-survai-density", density.key);
  }

  return preset;
}
